import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Aurora from "../../components/shell/Aurora";
import NexusBrand from "../../components/brand/NexusBrand";
import PublicIcon from "../../components/brand/PublicIcon";
import "../../styles/nexus-public.css";
import { SETUP_STEPS } from "../../components/brand/setupSteps";
import { useDocumentTitle } from "../../components/shell/pageTitle";

export default function AccountSetup() {
  useDocumentTitle("Setup under review");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const inactive =
    (user?.organizationActive ?? user?.organization_active) === false;
  const organizationName = user?.organizationName ?? user?.organization_name;

  const signOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    await logout();
    navigate("/login", { replace: true });
  };

  // Only the first step is known to be complete; the rest describe what
  // happens next and stay unticked until the NexusDialer team approves the
  // workspace. A paused workspace says so once, in the review step.
  const steps = SETUP_STEPS.map((step, index) => {
    if (index === 0)
      return {
        ...step,
        state: "done",
        title: step.doneTitle,
        text: "You are signed in.",
      };
    if (step.id === "review")
      return inactive
        ? {
            ...step,
            state: "current",
            title: "Workspace access",
            text: "Access paused.",
          }
        : { ...step, state: "current" };
    return { ...step, state: "later" };
  });

  return (
    <main className="nexus-public nx-auth-page nx-auth-page--setup">
      <Aurora spread="wide" />
      <header className="nx-public-header">
        <Link to="/" aria-label="NexusDialer home">
          <NexusBrand size="small" />
        </Link>
        <button
          type="button"
          onClick={signOut}
          disabled={signingOut}
          className="nx-public-button"
        >
          {signingOut ? "Signing out…" : "Sign out"}
        </button>
      </header>
      <section
        className="nx-auth-card nx-auth-card--wide"
        aria-labelledby="setup-title"
      >
        <span className="nx-setup-badge">
          <PublicIcon name="check" />
          Signed in securely
        </span>
        <h1 id="setup-title" className="nx-auth-title">
          {inactive
            ? "This workspace is paused."
            : "Your account is created. Setup is under review."}
        </h1>
        <p className="nx-auth-description">
          {inactive
            ? "Access to this workspace is paused. Contact the NexusDialer team to restore it."
            : "You’re signed in. The NexusDialer team reviews your workspace and helps complete setup before product access opens."}
        </p>
        <ol className="nx-setup-steps" aria-label="Setup progress">
          {steps.map((step, index) => (
            <li
              key={step.id}
              className={`is-${step.state}`}
              aria-current={step.state === "current" ? "step" : undefined}
            >
              <span className="nx-setup-dot" aria-hidden="true">
                {step.state === "done" ? (
                  <PublicIcon name="check" />
                ) : (
                  String(index + 1).padStart(2, "0")
                )}
              </span>
              <div>
                <strong>{step.title}</strong>
                <p>{step.text}</p>
              </div>
            </li>
          ))}
        </ol>
        <dl className="nx-setup-details">
          <div>
            <dt>Account</dt>
            <dd>{user?.email}</dd>
          </div>
          {organizationName && (
            <div>
              <dt>Workspace</dt>
              <dd>{organizationName}</dd>
            </div>
          )}
          <div>
            <dt>Status</dt>
            <dd
              className={`nx-setup-status${inactive ? " nx-setup-status--blocked" : ""}`}
            >
              {inactive ? "Workspace unavailable" : "Awaiting review"}
            </dd>
          </div>
        </dl>
        <div className="nx-auth-actions">
          <a
            href="mailto:support@ingeniousdigital.com?subject=NexusDialer%20workspace%20setup"
            className="nx-public-button nx-public-button--primary"
          >
            {inactive
              ? "Contact the NexusDialer team"
              : "Contact us to complete setup"}
          </a>
        </div>
        {!inactive && (
          <p className="nx-public-help">
            After the NexusDialer team approves your workspace, sign in again to
            start using NexusDialer.
          </p>
        )}
      </section>
    </main>
  );
}
